/**
 * Generic data export utility for GovCon tables.
 * Exports data as XLSX if the xlsx library is available,
 * otherwise falls back to CSV.
 */ /**
 * Export data to an Excel-compatible file.
 * Generates a proper XLSX blob using the xlsx library if available,
 * or falls back to CSV.
 */ export async function exportToExcel(options) {
    const { filename, sheetName = 'Sheet1', columns, data, format = 'xlsx' } = options;
    const headers = columns.map((c)=>c.header);
    const rows = data.map((row)=>columns.map((col)=>{
            if (typeof col.accessor === 'function') {
                return col.accessor(row);
            }
            return row[col.accessor] ?? '';
        }));
    if (format === 'csv') {
        return downloadCSV(filename, headers, rows);
    }
    // Try to use xlsx library (dynamic import)
    try {
        const XLSX = await import('xlsx');
        const ws = XLSX.utils.aoa_to_sheet([
            headers,
            ...rows
        ]);
        // Auto-width columns
        const colWidths = columns.map((col, i)=>{
            const maxLen = Math.max(col.header.length, ...rows.map((r)=>String(r[i]).length));
            return {
                wch: Math.min(maxLen + 2, 50)
            };
        });
        ws['!cols'] = colWidths;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `${filename}.xlsx`);
        return;
    } catch  {
    // xlsx library not available, fall back to CSV
    }
    downloadCSV(filename, headers, rows);
}
function downloadCSV(filename, headers, rows) {
    const csvContent = [
        headers.map((h)=>`"${h}"`).join(','),
        ...rows.map((row)=>row.map((cell)=>`"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([
        csvContent
    ], {
        type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}
/**
 * Pre-configured export for compliance matrix / shred view data.
 */ export function exportRequirements(data, format = 'xlsx') {
    return exportToExcel({
        filename: 'requirements',
        sheetName: 'Requirements',
        format,
        columns: [
            {
                header: 'Req ID',
                accessor: 'requirementId'
            },
            {
                header: 'Sequence',
                accessor: (r)=>r.sequenceNumber ?? ''
            },
            {
                header: 'Requirement Text',
                accessor: (r)=>r.source?.exactText || r.text || ''
            },
            {
                header: 'Category',
                accessor: 'category'
            },
            {
                header: 'Obligation',
                accessor: 'obligationStrength'
            },
            {
                header: 'Mandatory',
                accessor: (r)=>r.isMandatory ? 'Yes' : 'No'
            },
            {
                header: 'Evaluated',
                accessor: (r)=>r.isEvaluated ? 'Yes' : 'No'
            },
            {
                header: 'Eval Factor',
                accessor: (r)=>r.evaluationFactor || ''
            },
            {
                header: 'Status',
                accessor: 'status'
            },
            {
                header: 'Source File',
                accessor: (r)=>r.source?.filename || ''
            },
            {
                header: 'Section',
                accessor: (r)=>r.source?.sectionNumber || ''
            },
            {
                header: 'Page',
                accessor: (r)=>r.source?.pageNumber > 0 ? r.source.pageNumber : ''
            },
            {
                header: 'Section Path',
                accessor: (r)=>r.sectionContext?.fullPath || ''
            },
            {
                header: 'Response Action',
                accessor: (r)=>r.responseAction || ''
            },
            {
                header: 'Deliverable Type',
                accessor: (r)=>r.deliverableType || ''
            },
            {
                header: 'Eval Subfactor',
                accessor: (r)=>r.evaluationSubfactor || ''
            },
            {
                header: 'Eval Weight',
                accessor: (r)=>r.evaluationWeight != null ? `${r.evaluationWeight}%` : ''
            },
            {
                header: 'Entities',
                accessor: (r)=>(r.entities || []).join(', ')
            },
            {
                header: 'Categories',
                accessor: (r)=>(r.categories || []).join(', ')
            },
            {
                header: 'Related CLINs',
                accessor: (r)=>(r.relatedCLINs || []).join(', ')
            },
            {
                header: 'Amendment',
                accessor: (r)=>r.amendmentInfo?.isFromAmendment ? `Amd ${r.amendmentInfo.amendmentNumber || '?'}` : ''
            },
            {
                header: 'Proposal Section',
                accessor: (r)=>r.mapping?.proposalSection || ''
            },
            {
                header: 'Notes',
                accessor: (r)=>r.notes || ''
            },
            {
                header: 'Confidence',
                accessor: (r)=>r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : ''
            }
        ],
        data
    });
}
/** @deprecated Use exportRequirements instead */ export const exportComplianceMatrix = exportRequirements;
/**
 * Pre-configured export for CLINs data (no labor categories).
 */ export function exportCLINs(data, format = 'xlsx') {
    return exportToExcel({
        filename: 'clins',
        sheetName: 'CLINs',
        format,
        columns: [
            {
                header: 'CLIN #',
                accessor: 'clinNumber'
            },
            {
                header: 'Parent CLIN',
                accessor: (r)=>r.parentClin || ''
            },
            {
                header: 'Title',
                accessor: 'title'
            },
            {
                header: 'Description',
                accessor: (r)=>r.description || ''
            },
            {
                header: 'Contract Type',
                accessor: (r)=>r.contractType || ''
            },
            {
                header: 'Option Type',
                accessor: (r)=>r.optionType || ''
            },
            {
                header: 'Optional Task',
                accessor: (r)=>r.isOptionalTask ? `OT${r.optionalTaskNumber || ''}` : ''
            },
            {
                header: 'Period',
                accessor: (r)=>r.periodName || ''
            },
            {
                header: 'Quantity',
                accessor: (r)=>r.pricing?.quantity || ''
            },
            {
                header: 'Unit',
                accessor: (r)=>r.pricing?.unit || ''
            },
            {
                header: 'Unit Price',
                accessor: (r)=>r.pricing?.unitPrice || ''
            },
            {
                header: 'Extended Price',
                accessor: (r)=>r.pricing?.extendedPrice || ''
            },
            {
                header: 'Ceiling',
                accessor: (r)=>r.pricing?.ceiling || ''
            },
            {
                header: 'Corresponding Base CLIN',
                accessor: (r)=>r.correspondingBaseClin || ''
            },
            {
                header: 'PWS References',
                accessor: (r)=>(r.pwsReferences || []).join(', ')
            },
            {
                header: 'Attachment References',
                accessor: (r)=>(r.attachmentReferences || []).join(', ')
            },
            {
                header: 'Source Page',
                accessor: (r)=>r.source?.pageNumber > 0 ? r.source.pageNumber : ''
            },
            {
                header: 'Status',
                accessor: (r)=>r.status || ''
            },
            {
                header: 'Notes',
                accessor: (r)=>r.notes || ''
            }
        ],
        data
    });
}
/**
 * Pre-configured export for CLINs with labor categories.
 * Flattens deduped LCATs back to per-CLIN rows using clinLinkages,
 * so each row is a CLIN + role pair with that CLIN's specific reasoning.
 * Sorted by CLIN number for a CLIN-first layout.
 */ export function exportCLINsWithLCATs(clins, lcats, format = 'xlsx') {
    const clinMap = new Map();
    for (const clin of clins){
        clinMap.set(clin.clinNumber, clin);
    }
    // Flatten: one row per CLIN-role pair using clinLinkages
    const rows = [];
    for (const lcat of lcats){
        if (lcat.clinLinkages?.length) {
            for (const linkage of lcat.clinLinkages){
                const clin = clinMap.get(linkage.clinNumber) || {};
                rows.push({
                    ...lcat,
                    _clin: clin,
                    _clinNumber: linkage.clinNumber,
                    _reasoning: linkage.reasoning || lcat.reasoning || '',
                    _confidence: linkage.confidence ?? lcat.confidence,
                    _pwsRefs: linkage.pwsReferences || lcat.pwsReferences || [],
                    _attachRefs: linkage.attachmentReferences || lcat.attachmentReferences || []
                });
            }
        } else {
            // Fallback for LCATs without clinLinkages
            const clin = clinMap.get(lcat.clinNumber || lcat.category) || {};
            rows.push({
                ...lcat,
                _clin: clin,
                _clinNumber: lcat.clinNumber || lcat.category || '',
                _reasoning: lcat.reasoning || '',
                _confidence: lcat.confidence,
                _pwsRefs: lcat.pwsReferences || [],
                _attachRefs: lcat.attachmentReferences || []
            });
        }
    }
    // Sort by CLIN number so rows are grouped by CLIN
    rows.sort((a, b)=>(a._clinNumber || '').localeCompare(b._clinNumber || ''));
    return exportToExcel({
        filename: 'labor-categories',
        sheetName: 'Labor Categories',
        format,
        columns: [
            {
                header: 'CLIN #',
                accessor: (r)=>r._clinNumber
            },
            {
                header: 'CLIN Title',
                accessor: (r)=>r._clin?.title || ''
            },
            {
                header: 'CLIN Contract Type',
                accessor: (r)=>r._clin?.contractType || ''
            },
            {
                header: 'CLIN Period',
                accessor: (r)=>r._clin?.periodName || ''
            },
            {
                header: 'Labor Category',
                accessor: 'title'
            },
            {
                header: 'Description',
                accessor: (r)=>r.description || ''
            },
            {
                header: 'Team',
                accessor: (r)=>r.teamName || ''
            },
            {
                header: 'Education Level',
                accessor: (r)=>r.education?.level || r.level || ''
            },
            {
                header: 'Education Field',
                accessor: (r)=>r.education?.field || ''
            },
            {
                header: 'Min Experience (yrs)',
                accessor: (r)=>r.experience?.minimumYears ?? ''
            },
            {
                header: 'Clearance',
                accessor: (r)=>r.clearance || ''
            },
            {
                header: 'Certifications',
                accessor: (r)=>(r.certifications || []).join(', ')
            },
            {
                header: 'Hourly Rate',
                accessor: (r)=>r.baseHourlyRate || ''
            },
            {
                header: 'Ceiling Rate',
                accessor: (r)=>r.ceilingRate || ''
            },
            {
                header: 'Key Personnel',
                accessor: (r)=>r.isKeyPersonnel ? 'Yes' : ''
            },
            {
                header: 'AI Reasoning',
                accessor: (r)=>r._reasoning
            },
            {
                header: 'Confidence',
                accessor: (r)=>r._confidence != null ? `${(r._confidence * 100).toFixed(0)}%` : ''
            },
            {
                header: 'PWS References',
                accessor: (r)=>r._pwsRefs.join(', ')
            },
            {
                header: 'Attachment References',
                accessor: (r)=>r._attachRefs.join(', ')
            },
            {
                header: 'Status',
                accessor: (r)=>r.status || ''
            },
            {
                header: 'Notes',
                accessor: (r)=>r.notes || ''
            }
        ],
        data: rows
    });
}
/**
 * Pre-configured export for deduped labor categories (roles view).
 * One row per unique labor category, with linked CLINs as a comma-separated list.
 * Sorted alphabetically by title.
 */ export function exportLaborCategories(data, format = 'xlsx') {
    const sorted = [
        ...data
    ].sort((a, b)=>(a.title || '').localeCompare(b.title || ''));
    return exportToExcel({
        filename: 'labor-categories',
        sheetName: 'Labor Categories',
        format,
        columns: [
            {
                header: 'Labor Category',
                accessor: 'title'
            },
            {
                header: 'Description',
                accessor: (r)=>r.description || ''
            },
            {
                header: 'Team',
                accessor: (r)=>r.teamName || ''
            },
            {
                header: 'Education Level',
                accessor: (r)=>r.education?.level || r.level || ''
            },
            {
                header: 'Education Field',
                accessor: (r)=>r.education?.field || ''
            },
            {
                header: 'Min Experience (yrs)',
                accessor: (r)=>r.experience?.minimumYears ?? ''
            },
            {
                header: 'Clearance',
                accessor: (r)=>r.clearance || ''
            },
            {
                header: 'Certifications',
                accessor: (r)=>(r.certifications || []).join(', ')
            },
            {
                header: 'Key Personnel',
                accessor: (r)=>r.isKeyPersonnel ? 'Yes' : ''
            },
            {
                header: 'Hourly Rate',
                accessor: (r)=>r.baseHourlyRate || ''
            },
            {
                header: 'Ceiling Rate',
                accessor: (r)=>r.ceilingRate || ''
            },
            {
                header: 'Linked CLINs',
                accessor: (r)=>(r.clinLinkages || []).map((l)=>l.clinNumber).join(', ') || r.clinNumber || ''
            },
            {
                header: 'PWS References',
                accessor: (r)=>(r.pwsReferences || []).join(', ')
            },
            {
                header: 'Status',
                accessor: (r)=>r.status || ''
            },
            {
                header: 'Notes',
                accessor: (r)=>r.notes || ''
            },
            {
                header: 'Confidence',
                accessor: (r)=>r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : ''
            }
        ],
        data: sorted
    });
}
/**
 * Pre-configured export for deliverables data.
 */ export function exportDeliverables(data, format = 'xlsx') {
    return exportToExcel({
        filename: 'deliverables',
        sheetName: 'Deliverables',
        format,
        columns: [
            {
                header: 'CDRL #',
                accessor: 'cdrlNumber'
            },
            {
                header: 'Title',
                accessor: 'title'
            },
            {
                header: 'Status',
                accessor: 'status'
            },
            {
                header: 'Cadence',
                accessor: 'cadence'
            },
            {
                header: 'Due Date',
                accessor: 'currentDueDate'
            },
            {
                header: 'Days Until Due',
                accessor: 'daysUntilDue'
            },
            {
                header: 'Submissions',
                accessor: 'totalSubmissions'
            }
        ],
        data
    });
}
